import { useCallback, useState } from 'react';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useUpdateSuite } from '@/features/suites/hooks/suites';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { SuiteEditValues } from '@/features/suites/components/SuitesTable.types';
import type { TagColor } from '@/shared/data/tags';
import type { SuiteListItemDto } from '@flowqa/shared';

type SuitesTableEditDialog = {
  editEditor: ReturnType<typeof useDetailEditEditor<string>>;
  editErrors: ReturnType<typeof useFormErrors<SuiteEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<SuiteEditValues>>;
  editTarget: SuiteListItemDto | null;
  handleEditClose: () => void;
  handleEditRow: (row: SuiteListItemDto) => void;
  handleEditSave: (input: { tags: string[]; colors: TagColor[] }) => Promise<void>;
};

export const useSuitesTableEditDialog = (): SuitesTableEditDialog => {
  const toErrorMessage = useApiErrorMessage();
  const [editTarget, setEditTarget] = useState<SuiteListItemDto | null>(null);
  const editForm = useDialogFormState<SuiteEditValues>({ name: '', projectId: '' });
  const editErrors = useFormErrors(editForm);
  const updateSuite = useUpdateSuite();
  const editEditor = useDetailEditEditor({
    projectId: editTarget?.projectId ?? null,
    successMessage: getToastMessage('entity.updated', {
      entity: 'suite',
      name: editTarget?.name,
    }),
    saveEntity: async ({ name, tags }) => {
      if (!editTarget) return;
      await updateSuite.mutateAsync({
        id: editTarget.id,
        name: name ?? editForm.values.name,
        tags,
      });
    },
  });

  const handleEditRow = useCallback(
    (row: SuiteListItemDto): void => {
      setEditTarget(row);
      editForm.setField('name', row.name);
      editForm.setField('projectId', row.projectId);
    },
    [editForm],
  );

  const handleEditClose = useCallback((): void => {
    if (editEditor.isPending) return;
    editForm.handleClose(() => {
      setEditTarget(null);
    });
  }, [editEditor.isPending, editForm]);

  const handleEditSave = useCallback(
    async ({ tags, colors }: { tags: string[]; colors: TagColor[] }): Promise<void> => {
      if (!editTarget) return;
      const valid = editForm.validateRequired('name');
      if (!valid) return;
      try {
        await editEditor.save({ name: editForm.values.name, tags, colors });
        editForm.handleClose(() => {
          setEditTarget(null);
        });
      } catch (error) {
        if (!editForm.applyApiErrors(error)) {
          editForm.setSubmitError(toErrorMessage(error));
        }
      }
    },
    [editEditor, editForm, editTarget, toErrorMessage],
  );

  return {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  };
};
